/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import './style/reviewAnalytics.scss'

import React, {MouseEventHandler, useMemo} from 'react';
import {AwesomeIcon, ChangeFunction, Loader, SelectItemShape} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {BolmateButton, BolmateSelect} from '../controls';
import {ReviewChartData, ReviewSalesPeriod, ReviewStatsDisplay, ReviewTab} from '../../util/interfaces';
import classNames from 'classnames';
import ReviewsTableController from "./reviewsTable/reviewsTableController";
import ReviewFeedController from './reviewFeed/reviewFeedController';
import RatingExportsController from "./export/ratingExportsController";
import ReviewChart from "./reviewChart/reviewChart";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {parseThousandSeparator} from "../../util/utils";


interface Props {
    onChangePeriod: ChangeFunction<ReviewSalesPeriod>;
    onChangeTab: (tab: ReviewTab) => MouseEventHandler;
    onChangeStatsDisplay: MouseEventHandler;
    onClickStat: (stat: 'all' | 'positive' | 'negative') => MouseEventHandler;
    periodValue: ReviewSalesPeriod;
    tab: ReviewTab;
    fromDate: IDate | null;
    toDate: IDate | null;
    allowedTabs: Array<ReviewTab>;
    chartData: Record<string, ReviewChartData>;
    loadingChart: boolean;
    loadingStats: boolean;
    totalPositive: number;
    totalNegative: number;
    totalPositiveIncrease: number;
    totalNegativeIncrease: number;
    statsDisplay: ReviewStatsDisplay;
}

const periodItems: SelectItemShape[] = [
    {label: 'Afgelopen 7 dagen', value: ReviewSalesPeriod.SEVEN_DAYS},
    {label: 'Afgelopen 30 dagen', value: ReviewSalesPeriod.THIRTY_DAYS},
    {label: 'Gehele periode', value: ReviewSalesPeriod.TOTAL},
    {label: 'Aangepast...', value: ReviewSalesPeriod.CUSTOM},
]

export default function ReviewAnalytics(props: Props) {
    const {
        onChangePeriod, periodValue, tab, onChangeTab, allowedTabs, fromDate, toDate, chartData,
        loadingChart, loadingStats, totalPositiveIncrease, totalNegativeIncrease, totalPositive, totalNegative,
        onChangeStatsDisplay, statsDisplay, onClickStat
    } = props;
    const totalReviews = useMemo(() => {
        return totalPositive + totalNegative;
    }, [totalPositive, totalNegative]);
    const totalIncrease = useMemo(() => {
        return totalPositiveIncrease + totalNegativeIncrease;
    }, [totalPositiveIncrease, totalNegativeIncrease]);
    const periodLabel = useMemo(() => {
        let days: number = 0;
        if (periodValue === ReviewSalesPeriod.TOTAL) return 'GEHELE PERIODE';
        if (fromDate && toDate) days = toDate.absDiff(fromDate, 'days');
        else if (periodValue === ReviewSalesPeriod.THIRTY_DAYS) days = 30;
        else if (periodValue === ReviewSalesPeriod.SEVEN_DAYS) days = 7;
        return `AFGELOPEN ${days} dag${days === 1 ? '' : 'en'}`;
    }, [periodValue, fromDate, toDate]);
    const statDisplayLabel = useMemo(() => {
        if (statsDisplay === ReviewStatsDisplay.STATS_ONLY) return 'Grafiek tonen';
        return 'Grafiek verbergen';
    }, [statsDisplay]);

    function renderTop() {
        return (
            <div className='top-container full'>
                <ReviewChart chartData={chartData} loading={loadingChart}/>
                {renderStats()}
                <div className='period-container'>
                    <BolmateSelect value={periodValue} onChange={onChangePeriod} items={periodItems}
                                   itemsHeight='30rem' highlightSelected itemContainerClassName='period-items-container'
                                   className='reviews-period-select'/>
                    <BolmateButton onClick={onChangeStatsDisplay} label={statDisplayLabel} disabled={loadingStats}
                                   className='btn-stat-display' secondary/>
                </div>
            </div>
        );
    }

    function renderTopStatsOnly() {
        return (
            <div className='top-container stats-only'>
                {renderStats()}
                <div className='period-container'>
                    <BolmateSelect value={periodValue} onChange={onChangePeriod} items={periodItems}
                                   itemsHeight='30rem' highlightSelected itemContainerClassName='period-items-container'
                                   className='reviews-period-select'/>
                    <BolmateButton onClick={onChangeStatsDisplay} label={statDisplayLabel} disabled={loadingStats}
                                   className='btn-stat-display' secondary/>
                </div>
            </div>
        )
    }

    function renderStats() {
        return (
            <div className='review-stats-container'>
                {renderStat('TOTAAL REVIEWS', totalReviews, totalIncrease, 'star', 'total', onClickStat('all'))}
                {renderStat('POSITIEVE REVIEWS', totalPositive, totalPositiveIncrease, 'face-smile', 'positive',
                    onClickStat('positive'))}
                {renderStat('NEGATIEVE REVIEWS', totalNegative, totalNegativeIncrease, 'face-frown', 'negative',
                    onClickStat('negative'))}
            </div>
        )
    }

    function renderStat(title: string, total: number, increase: number, icon: IconName, className: string,
                        onClick?: MouseEventHandler) {
        return (
            <div className={classNames('review-stat-container', className)}>
                <Loader active={loadingStats} size='2.5rem'/>
                <div className='icon-container'>
                    <AwesomeIcon name={icon} className='stat-icon' prefix='fas'/>
                </div>
                <label className='stat-title'>{title}</label>
                <label className='stat-total'>{parseThousandSeparator(total)}</label>
                <label className='increase-container'><span className='increase-label'>+{parseThousandSeparator(
                    increase)}</span> {periodLabel} {!!onClick &&
                    <span className='click-label' onClick={onClick}>(bekijken)</span>}</label>
            </div>
        )
    }

    function renderTabs() {
        return (
            <div className='tabs-container'>
                {allowedTabs.map(renderTab)}
            </div>
        )
    }

    function renderTab(_tab: ReviewTab, index: number) {
        const isActive = _tab === tab;
        let label;
        if (_tab === ReviewTab.PRODUCTS) label = 'Producten';
        else if (_tab === ReviewTab.FEED) label = 'Feed';
        else if (_tab === ReviewTab.EXPORT) label = 'Exporteren';
        return (
            <div key={index} className={classNames('tab', isActive && 'active')}
                 onClick={onChangeTab(_tab)}>
                <label className='tab-label'>{label}</label>
            </div>
        )
    }

    function renderTable() {
        return (
            <ReviewsTableController period={periodValue} fromDate={fromDate} toDate={toDate}/>
        );
    }

    return (
        <div className='review-analytics'>
            {statsDisplay === ReviewStatsDisplay.FULL ? renderTop() : renderTopStatsOnly()}
            {renderTabs()}
            {tab === ReviewTab.PRODUCTS && renderTable()}
            {tab === ReviewTab.FEED && <ReviewFeedController/>}
            {tab === ReviewTab.EXPORT && <RatingExportsController/>}
        </div>
    )
}